import React, { useState, useEffect, useRef } from 'react';
import supportRequestsApi from '../services/support-requests';
import { useTri, EnteteTri } from './planning/TriTableau';
import './SupportRequests.css';
import './Litiges.css';

const STATUTS = {
  en_attente_constructeur: { label: 'En attente constructeur', classe: 'sr-badge-attente-constructeur', dot: '#F59E0B' },
  en_attente_interne:      { label: 'En attente de notre part', classe: 'sr-badge-attente-interne', dot: '#EF4444' },
  resolu:                  { label: 'Résolu',                   classe: 'sr-badge-resolu',              dot: '#10B981' },
  cloture:                 { label: 'Clôturé',                  classe: 'sr-badge-cloture',             dot: '#6B7280' },
};

// ─── Compression image côté client ─────────────────────────────────────────
async function compressImage(file) {
  if (!file.type.startsWith('image/')) return file;
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) resolve(file);
          else resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
        },
        'image/jpeg', 0.82
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
    img.src = objectUrl;
  });
}

// ─── Vignette doc (copie exacte de CommentDocThumb de Litiges) ──────────────
function CommentDocThumb({ doc }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const mimeType = doc.type || doc.type_mime || '';
  const isImage = mimeType.startsWith('image/');
  const isPdf   = mimeType === 'application/pdf';
  const url     = doc.url || null;
  return (
    <>
      <div className="doc-thumbnail" title={doc.name}>
        <div className="doc-preview" onClick={() => url && setLightboxOpen(true)}>
          {url && isImage ? (
            <img src={url} alt={doc.name} />
          ) : (
            <div className="doc-icon">
              <span className="doc-icon-emoji">{isPdf ? '📄' : '📎'}</span>
              <span className="doc-icon-label">{isPdf ? 'PDF' : 'DOC'}</span>
            </div>
          )}
        </div>
        <div className="doc-name">{doc.name}</div>
      </div>
      {lightboxOpen && url && (
        <div className="sr-lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <div className="sr-lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="sr-lightbox-close" onClick={() => setLightboxOpen(false)}>✕</button>
            {isImage ? (
              <img src={url} alt={doc.name} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px' }} />
            ) : (
              <iframe src={url} title={doc.name} style={{ width: '80vw', height: '85vh', border: 'none', borderRadius: '8px' }} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function SupportRequests({ user, editMode = false }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tri partagé (planning/TriTableau) : sans clé, l'ordre du serveur est conservé.
  const tri = useTri(null, 'asc');
  const [search, setSearch] = useState('');
  const [filterMarque, setFilterMarque] = useState('');
  // Concession : suit le choix du menu (2026-10-26) — la page est remontée à chaque changement.
  const [filterConcession] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [marques, setMarques] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [form, setForm] = useState({ date_demande: new Date().toISOString().split('T')[0], marque: '', objet: '', reference_constructeur: '', immatriculation: '', numero_or: '' });
  const [sending, setSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  // Documents attachés aux messages (state séparé, comme Litiges)
  const [attachmentDocs, setAttachmentDocs] = useState([]); // { message_id, url, name, type }
  // Pièce jointe sur le message en cours
  const [pendingDoc, setPendingDoc] = useState(null); // { file, url, name }
  const [docDragging, setDocDragging] = useState(false);
  const msgFileInputRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    supportRequestsApi.getMarques().then(res => setMarques(res.data.marques || [])).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    loadRequests();
  }, [filterMarque, filterConcession]); // eslint-disable-line

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterMarque) params.marque = filterMarque;
      if (filterConcession) params.concession_id = filterConcession;
      if (search) params.search = search;

      const reqRes = await supportRequestsApi.getAll(params);
      setRequests(reqRes.data.requests);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadRequests();
  };

  const openDetail = async (req) => {
    try {
      const res = await supportRequestsApi.getById(req.id);
      const msgs = res.data.messages;
      setSelectedRequest(res.data.request);
      setIsEditing(false);
      setMessages(msgs);
      // Alimenter attachmentDocs depuis les messages qui ont une pièce jointe (comme loadDocuments dans Litiges)
      setAttachmentDocs(
        msgs.filter(m => m.attachment_url).map(m => ({
          message_id: m.id, url: m.attachment_url, name: m.attachment_name, type: m.attachment_type,
        }))
      );
      setTimeout(() => {
        if (timelineRef.current) timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
      }, 100);
    } catch (error) {
      console.error('Erreur ouverture détail:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.marque || !form.objet) return alert('Marque et objet obligatoires');
    try {
      await supportRequestsApi.create(form);
      setShowCreateModal(false);
      setForm({ date_demande: new Date().toISOString().split('T')[0], marque: '', objet: '', reference_constructeur: '', immatriculation: '', numero_or: '' });
      loadRequests();
    } catch (error) {
      console.error('Erreur création:', error);
      alert('Erreur lors de la création');
    }
  };

  const sendMessage = async (expediteur) => {
    if (!newMessage.trim() || !selectedRequest) return;
    try {
      setSending(true);
      const res = await supportRequestsApi.addMessage(selectedRequest.id, { expediteur, contenu: newMessage.trim() });
      const messageId = res.data.message?.id;

      // Upload du doc lié si présent (même logique que Litiges handleAddComment)
      if (pendingDoc && messageId) {
        try {
          const file = pendingDoc.file;
          const fileToUpload = file.type.startsWith('image/') ? await compressImage(file) : file;
          const fd = new FormData();
          fd.append('file', fileToUpload, fileToUpload.name);
          fd.append('message_id', String(messageId));
          const uploadRes = await supportRequestsApi.uploadMessageFile(selectedRequest.id, fd);
          const { attachment_url } = uploadRes.data;
          // Ajouter dans attachmentDocs (comme Litiges fait loadDocuments après upload)
          setAttachmentDocs(prev => [...prev, {
            message_id: messageId,
            url: attachment_url,
            name: pendingDoc.name,
            type: file.type,
          }]);
        } catch (docErr) {
          console.error('Erreur upload pièce jointe:', docErr);
        }
        if (pendingDoc.url?.startsWith('blob:')) URL.revokeObjectURL(pendingDoc.url);
        setPendingDoc(null);
      }

      setNewMessage('');
      // Recharger les messages (comme Litiges recharge commentaires + documents)
      const detail = await supportRequestsApi.getById(selectedRequest.id);
      setSelectedRequest(detail.data.request);
      setMessages(detail.data.messages);
      loadRequests();
      setTimeout(() => {
        if (timelineRef.current) timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
      }, 100);
    } catch (error) {
      console.error('Erreur envoi message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleDocDrop = (e) => {
    e.preventDefault();
    setDocDragging(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    if (pendingDoc?.url?.startsWith('blob:')) URL.revokeObjectURL(pendingDoc.url);
    setPendingDoc({ file, url: URL.createObjectURL(file), name: file.name });
  };

  const updateStatut = async (newStatut) => {
    if (!selectedRequest) return;
    try {
      await supportRequestsApi.update(selectedRequest.id, { statut: newStatut });
      setSelectedRequest({ ...selectedRequest, statut: newStatut });
      loadRequests();
    } catch (error) {
      console.error('Erreur maj statut:', error);
    }
  };

  const startEdit = () => {
    if (!selectedRequest) return;
    setEditForm({
      date_demande: selectedRequest.date_demande ? selectedRequest.date_demande.split('T')[0] : '',
      marque: selectedRequest.marque || '',
      objet: selectedRequest.objet || '',
      reference_constructeur: selectedRequest.reference_constructeur || '',
      immatriculation: selectedRequest.immatriculation || '',
      numero_or: selectedRequest.numero_or || '',
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedRequest || !editForm) return;
    if (!editForm.marque.trim() || !editForm.objet.trim()) return alert('Marque et objet obligatoires');
    try {
      const res = await supportRequestsApi.update(selectedRequest.id, {
        date_demande: editForm.date_demande || null,
        marque: editForm.marque.trim(),
        objet: editForm.objet.trim(),
        reference_constructeur: editForm.reference_constructeur.trim(),
        immatriculation: editForm.immatriculation.trim().toUpperCase(),
        numero_or: editForm.numero_or.trim(),
      });
      setSelectedRequest(res.data.request);
      setIsEditing(false);
      setEditForm(null);
      loadRequests();
    } catch (error) {
      console.error('Erreur modification demande:', error);
      alert(error.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleDelete = async () => {
    if (!selectedRequest) return;
    if (!window.confirm('Supprimer cette demande et tout son historique ?')) return;
    try {
      await supportRequestsApi.delete(selectedRequest.id);
      setSelectedRequest(null);
      loadRequests();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const daysSince = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const now = new Date();
    return Math.floor((now - d) / (1000 * 60 * 60 * 24));
  };

  // Le tri porte sur la donnée brute et non sur l'affichage : « Attente » se
  // trie en jours (nombre) et non sur « 12j », « Statut » sur son libellé lisible
  // et non sur la clé technique — trier par statut donnerait sinon un ordre
  // (en_attente_constructeur, en_attente_interne…) que personne ne reconnaît.
  const triees = tri.appliquer(requests, {
    date: r => r.date_demande,
    marque: r => r.marque,
    objet: r => r.objet,
    reference: r => r.reference_constructeur,
    immat: r => r.immatriculation,
    statut: r => (STATUTS[r.statut] || {}).label || r.statut,
    echange: r => (r.dernier_echange ? Date.parse(r.dernier_echange) : null),
    attente: r => ((r.statut === 'en_attente_constructeur' || r.statut === 'en_attente_interne')
      ? daysSince(r.dernier_echange || r.created_at) : null),
  });

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="page-container">
      {/* Barre d'actions */}
      <div className="sr-actions-bar">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
          <input
            className="sr-search-input"
            placeholder="Rechercher (objet, marque, immat, ref.)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button type="submit" className="sr-btn-new" style={{ background: '#6b7280' }}>Chercher</button>
        </form>
        <select
          className="sr-search-input"
          style={{ minWidth: '160px', flex: 'none' }}
          value={filterMarque}
          onChange={e => setFilterMarque(e.target.value)}
        >
          <option value="">Tous constructeurs</option>
          {marques.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <button className="sr-btn-new" onClick={() => setShowCreateModal(true)}>
          + Nouvelle demande
        </button>
      </div>

      {/* Tableau principal */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <EnteteTri tri={tri} cle="date">Date</EnteteTri>
              <EnteteTri tri={tri} cle="marque">Marque</EnteteTri>
              <EnteteTri tri={tri} cle="objet">Objet</EnteteTri>
              <EnteteTri tri={tri} cle="reference">Réf. constructeur</EnteteTri>
              <EnteteTri tri={tri} cle="immat">Immat.</EnteteTri>
              <EnteteTri tri={tri} cle="statut">Statut</EnteteTri>
              <EnteteTri tri={tri} cle="echange">Dernier échange</EnteteTri>
              <EnteteTri tri={tri} cle="attente">Attente</EnteteTri>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Chargement...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Aucune demande trouvée</td></tr>
            ) : triees.map(req => {
              const days = daysSince(req.dernier_echange || req.created_at);
              const statutInfo = STATUTS[req.statut] || STATUTS.en_attente_constructeur;
              return (
                <tr key={req.id} className="sr-clickable-row" onClick={() => openDetail(req)}>
                  <td>{formatDate(req.date_demande)}</td>
                  <td style={{ fontWeight: 600 }}>{req.marque}</td>
                  <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.objet}</td>
                  <td>{req.reference_constructeur || '—'}</td>
                  <td>{req.immatriculation || '—'}</td>
                  <td><span className={`sr-badge ${statutInfo.classe}`}>{statutInfo.label}</span></td>
                  <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{formatDateTime(req.dernier_echange)}</td>
                  <td>
                    {(req.statut === 'en_attente_constructeur' || req.statut === 'en_attente_interne') && days !== null && (
                      <span className={`sr-days ${days > 7 ? 'urgent' : days > 3 ? 'warning' : ''}`}>
                        {days}j
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal création */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle demande d'assistance</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Date de la demande</label>
                  <input type="date" value={form.date_demande} onChange={e => setForm({ ...form, date_demande: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Marque *</label>
                  <input type="text" value={form.marque} onChange={e => setForm({ ...form, marque: e.target.value })} placeholder="Ex: Honda, Suzuki..." required />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Objet de la demande *</label>
                  <textarea value={form.objet} onChange={e => setForm({ ...form, objet: e.target.value })} placeholder="Décrivez le problème technique..." rows={3} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }} required />
                </div>
                <div className="form-group">
                  <label>Réf. constructeur</label>
                  <input type="text" value={form.reference_constructeur} onChange={e => setForm({ ...form, reference_constructeur: e.target.value })} placeholder="N° de ticket / dossier" />
                </div>
                <div className="form-group">
                  <label>Immatriculation</label>
                  <input type="text" value={form.immatriculation} onChange={e => setForm({ ...form, immatriculation: e.target.value.toUpperCase() })} placeholder="AB-123-CD" />
                </div>
                <div className="form-group">
                  <label>N° OR</label>
                  <input type="text" value={form.numero_or} onChange={e => setForm({ ...form, numero_or: e.target.value })} placeholder="N° ordre de réparation" />
                </div>
              </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Annuler</button>
                <button type="submit" className="btn-primary">Créer la demande</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal détail / échanges */}
      {selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content sr-detail-modal" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="modal-header">
              <div style={{ flex: 1 }}>
                {isEditing ? (
                  <textarea
                    className="sr-edit-objet"
                    value={editForm.objet}
                    onChange={e => setEditForm({ ...editForm, objet: e.target.value })}
                    rows={2}
                  />
                ) : (
                  <h2 style={{ margin: '0 0 6px 0', fontSize: '18px' }}>{selectedRequest.objet}</h2>
                )}
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Créée le {formatDate(selectedRequest.date_demande)} par {selectedRequest.user_prenom} {selectedRequest.user_nom}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {isEditing ? (
                  <>
                    <button className="btn-primary btn-sm" onClick={handleSaveEdit}>Enregistrer</button>
                    <button className="btn-secondary btn-sm" onClick={cancelEdit}>Annuler</button>
                  </>
                ) : (
                  <>
                    <select className="sr-statut-select" value={selectedRequest.statut} onChange={e => updateStatut(e.target.value)}>
                      {Object.entries(STATUTS).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                      ))}
                    </select>
                    {user?.role === 'super-admin' && <button className="btn-secondary btn-sm" onClick={startEdit} title="Modifier la demande">✏️ Modifier</button>}
                    {editMode && <button className="btn-danger btn-sm" onClick={handleDelete} title="Supprimer">Supprimer</button>}
                  </>
                )}
                <button className="modal-close" onClick={() => setSelectedRequest(null)}>✕</button>
              </div>
            </div>

            <div className="modal-body">
            {/* Infos */}
            {isEditing ? (
              <div className="sr-detail-info sr-detail-info-edit">
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Date de la demande</span>
                  <input type="date" className="sr-edit-input" value={editForm.date_demande} onChange={e => setEditForm({ ...editForm, date_demande: e.target.value })} />
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Marque *</span>
                  <input type="text" className="sr-edit-input" value={editForm.marque} onChange={e => setEditForm({ ...editForm, marque: e.target.value })} />
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Réf. constructeur</span>
                  <input type="text" className="sr-edit-input" value={editForm.reference_constructeur} onChange={e => setEditForm({ ...editForm, reference_constructeur: e.target.value })} />
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Immatriculation</span>
                  <input type="text" className="sr-edit-input" value={editForm.immatriculation} onChange={e => setEditForm({ ...editForm, immatriculation: e.target.value.toUpperCase() })} />
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">N° OR</span>
                  <input type="text" className="sr-edit-input" value={editForm.numero_or} onChange={e => setEditForm({ ...editForm, numero_or: e.target.value })} />
                </div>
              </div>
            ) : (
              <div className="sr-detail-info">
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Marque</span>
                  <span className="sr-detail-info-value">{selectedRequest.marque}</span>
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Réf. constructeur</span>
                  <span className="sr-detail-info-value">{selectedRequest.reference_constructeur || '—'}</span>
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">Immatriculation</span>
                  <span className="sr-detail-info-value">{selectedRequest.immatriculation || '—'}</span>
                </div>
                <div className="sr-detail-info-item">
                  <span className="sr-detail-info-label">N° OR</span>
                  <span className="sr-detail-info-value">{selectedRequest.numero_or || '—'}</span>
                </div>
              </div>
            )}

            {/* Historique des échanges — structure identique à Litiges */}
            <div className="commentaires-section">
              <h3>💬 Historique des échanges</h3>
              <div className="commentaires-list" ref={timelineRef}>
                {messages.length === 0 ? (
                  <p style={{ color: 'light-dark(#6c757d, var(--text-secondary))', fontStyle: 'italic' }}>Aucun échange pour le moment.</p>
                ) : messages.map(msg => {
                  const msgDoc = attachmentDocs.find(d => d.message_id === msg.id);
                  return (
                    <div key={msg.id} className="commentaire-item">
                      <div className="commentaire-body">
                        <div className="commentaire-header">
                          <strong>{msg.expediteur === 'nous' ? (`${msg.user_prenom || ''} ${msg.user_nom || ''}`.trim() || 'Nous') : 'Constructeur'}</strong>
                          <span className="commentaire-date">
                            {new Date(msg.created_at).toLocaleDateString('fr-FR')} à {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="commentaire-text">{msg.contenu}</div>
                      </div>
                      {msgDoc && (
                        <div className="commentaire-docs">
                          <CommentDocThumb doc={msgDoc} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Nouveau message — structure identique à Litiges */}
            <div className="new-comment">
              <div className="new-comment-row">
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Saisissez le contenu de l'échange..."
                  rows="3"
                  className="new-comment-textarea"
                  disabled={sending}
                />
                <div
                  className={`comment-doc-drop${docDragging ? ' dragging' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDocDragging(true); }}
                  onDragLeave={() => setDocDragging(false)}
                  onDrop={handleDocDrop}
                  onClick={() => msgFileInputRef.current?.click()}
                >
                  {pendingDoc ? (
                    <div className="comment-doc-preview">
                      <CommentDocThumb doc={{ url: pendingDoc.url, name: pendingDoc.name, type: pendingDoc.file.type }} />
                      <button className="comment-doc-remove" onClick={e => { e.stopPropagation(); if (pendingDoc.url?.startsWith('blob:')) URL.revokeObjectURL(pendingDoc.url); setPendingDoc(null); }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <span className="comment-doc-icon">📎</span>
                      <span className="comment-doc-hint">Glisser un doc</span>
                    </>
                  )}
                  <input ref={msgFileInputRef} type="file" style={{ display: 'none' }} accept="image/*,application/pdf" onChange={handleDocDrop} />
                </div>
              </div>
              <div className="sr-send-buttons">
                <button className="sr-send-btn nous" onClick={() => sendMessage('nous')} disabled={sending || !newMessage.trim()}>
                  {sending ? 'Envoi...' : 'Notre message'}
                </button>
                <button className="sr-send-btn constructeur" onClick={() => sendMessage('constructeur')} disabled={sending || !newMessage.trim()}>
                  Rép. constructeur
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportRequests;
